import { useMemo } from 'react';
import { WarningAmber } from '@mui/icons-material';
import { Chip, Paper, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { DailyNavSetupCompany, FirmDailyNavCompanyConfigRow } from 'api';
import { dailyNavDateLabel } from 'common/constants/dailyNav';
import { Alert, Button, Skeleton, Stack, Text, Tooltip } from 'components/ui';
import { DAILY_NAV_SETUP_TEST_IDS } from '../DailyNavSetup.testIds';
import {
  COMPANY_SETUP_STATUS_LABELS,
  CompanySetupStatus,
  companySetupStatus,
  PreviousValuation,
  SetupDataState,
} from '../dailyNavSetup.utils';

type CompanyListStepProps = {
  companies: FirmDailyNavCompanyConfigRow[];
  setupByCompany: Map<number, DailyNavSetupCompany>;
  onSelectCompany: (companyId: number) => void;
  onStartWalkthrough: (companyId: number) => void;
  setupState: SetupDataState;
  setupErrorMessage?: string;
};

const STATUS_CHIP_COLORS: Record<CompanySetupStatus, 'success' | 'warning' | 'default'> = {
  [CompanySetupStatus.READY]: 'success',
  [CompanySetupStatus.NEEDS_ALEXANDRIA]: 'warning',
  [CompanySetupStatus.EXCLUDED]: 'default',
};

const CompanyListStep = ({
  companies,
  setupByCompany,
  onSelectCompany,
  onStartWalkthrough,
  setupState,
  setupErrorMessage,
}: CompanyListStepProps) => {
  const counts = useMemo(() => {
    const byStatus = { ready: 0, needs_alexandria: 0, excluded: 0 } as Record<CompanySetupStatus, number>;
    companies.forEach(row => {
      byStatus[companySetupStatus(row)] += 1;
    });
    return byStatus;
  }, [companies]);

  const nextPending = companies.find(row => companySetupStatus(row) === CompanySetupStatus.NEEDS_ALEXANDRIA);

  if (!companies.length) {
    return <Text variant="body2">This firm has no companies yet. Add companies, then come back here.</Text>;
  }

  return (
    <Stack spacing={3}>
      <div>
        <Text variant="h4" gutterBottom>
          Set up each company
        </Text>
        <Text variant="body2">
          A company is ready once it is included and linked to its external company profile. Adding comps is what gives it
          deltas on the grid, so it is worth doing while you&apos;re there. Exclude any company Daily NAV shouldn&apos;t
          cover.
        </Text>
      </div>
      {setupState === 'error' && (
        <Alert severity="error" data-testid={DAILY_NAV_SETUP_TEST_IDS.setupErrorAlert}>
          {setupErrorMessage ?? 'Previous valuations and GPC comps couldn\'t be loaded. Refresh the page to try again.'}
        </Alert>
      )}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
        <Text variant="body2" data-testid={DAILY_NAV_SETUP_TEST_IDS.companyCounts}>
          {companies.length} companies · {counts.ready} ready · {counts.needs_alexandria} need an external company profile ·{' '}
          {counts.excluded} excluded
        </Text>
        {/* Starts on the first company still needing a link, then steps through all of them. */}
        <Button
          variant={nextPending ? 'contained' : 'outlined'}
          onClick={() => onStartWalkthrough((nextPending ?? companies[0]).company_id)}
          data-testid={DAILY_NAV_SETUP_TEST_IDS.startWalkthroughButton}>
          {nextPending ? 'Start company setup' : 'Step through companies'}
        </Button>
      </Stack>
      {!nextPending && (
        <Alert severity="success">Every included company is linked. Continue to review.</Alert>
      )}
      <Paper>
        <Table size="small" data-testid={DAILY_NAV_SETUP_TEST_IDS.companyTable}>
          <TableHead>
            <TableRow>
              <TableCell>Company</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Comps</TableCell>
              <TableCell>Previous valuation</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {companies.map(row => {
              const status = companySetupStatus(row);
              const setup = setupByCompany.get(row.company_id);
              const previousValuation = (setup?.previous_valuation ?? null) as PreviousValuation;
              return (
                <TableRow key={row.company_id} hover data-testid={DAILY_NAV_SETUP_TEST_IDS.companyRow(row.company_id)}>
                  <TableCell>{row.company_name}</TableCell>
                  <TableCell>
                    <Chip size="small" color={STATUS_CHIP_COLORS[status]} label={COMPANY_SETUP_STATUS_LABELS[status]} />
                  </TableCell>
                  <TableCell>
                    {status === CompanySetupStatus.EXCLUDED || setupState !== 'ready' ? '—' : (setup?.comp_count ?? 0)}
                  </TableCell>
                  <TableCell>
                    {setupState === 'loading' && (
                      <Skeleton variant="text" width={90} height={16} data-testid={DAILY_NAV_SETUP_TEST_IDS.setupLoadingIndicator} />
                    )}
                    {setupState === 'error' && (
                      <Text variant="body2" color="text.secondary">
                        Unavailable
                      </Text>
                    )}
                    {setupState === 'ready' && previousValuation && dailyNavDateLabel(previousValuation.measurement_date)}
                    {setupState === 'ready' && !previousValuation && (
                      <Tooltip title="No final valuation yet, so Daily NAV has no previous valuation to compare against for this company.">
                        <Stack direction="row" spacing={0.5} alignItems="center" component="span">
                          <WarningAmber fontSize="small" color="warning" />
                          <span>None final</span>
                        </Stack>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant={status === CompanySetupStatus.NEEDS_ALEXANDRIA ? 'outlined' : 'text'}
                      onClick={() => onSelectCompany(row.company_id)}
                      data-testid={DAILY_NAV_SETUP_TEST_IDS.companySetupButton(row.company_id)}>
                      {status === CompanySetupStatus.NEEDS_ALEXANDRIA ? 'Set up' : 'Review'}
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>
    </Stack>
  );
};

export default CompanyListStep;
